import { invoke } from '@tauri-apps/api/core';
import { ipc_invoke } from '../ipc';

/**
 * Localized labels for the Windows system menu, passed across the IPC boundary
 * to `show_window_system_menu`. Rust deserializes this as
 * `SystemMenuLabels` (see `src-tauri/src/ipc/window.rs`).
 *
 * Property names match the deserialize contract — keep in lockstep with the
 * Rust struct (`#[serde(rename_all = "camelCase")]` + `#[serde(rename = "move")]`
 * on the Rust `move_` field).
 */
export type SystemMenuLabels = {
  restore: string;
  move: string;
  size: string;
  minimize: string;
  maximize: string;
  close: string;
};

/**
 * Tauri event broadcast by Rust after every successful zoom apply (keyboard
 * shortcut, macOS menu item, startup restore). Payload is the new clamped
 * factor. Keep in sync with `ZOOM_FACTOR_CHANGED_EVENT` in
 * `src-tauri/src/utils/zoom.rs`.
 */
export const ZOOM_FACTOR_CHANGED_EVENT = 'zoom://factor-changed';

class TauriWindowController {
  async expand_window(): Promise<boolean> {
    return ipc_invoke('expand_window', {}).then((res: { data: boolean }) => res.data);
  }

  async collapse_window(): Promise<boolean> {
    return ipc_invoke('collapse_window', {}).then((res: { data: boolean }) => res.data);
  }

  /**
   * Uses raw `invoke` rather than `ipc_invoke` because the Rust side returns
   * a bare `f64`, not an `IpcResponse` envelope — `ipc_invoke`'s `res.data`
   * unwrap would yield `undefined`.
   */
  async zoom_in(): Promise<number> {
    return invoke<number>('zoom_in');
  }

  /** See `zoom_in` above for why this bypasses `ipc_invoke`. */
  async zoom_out(): Promise<number> {
    return invoke<number>('zoom_out');
  }

  /** See `zoom_in` above for why this bypasses `ipc_invoke`. */
  async zoom_reset(): Promise<number> {
    return invoke<number>('zoom_reset');
  }

  /**
   * Read the current zoom factor without changing it. Called once on load to
   * seed `--app-zoom-factor` (see `TauriTitlebarHeightVar`); later changes
   * arrive via `ZOOM_FACTOR_CHANGED_EVENT`.
   *
   * Uses raw `invoke` rather than `ipc_invoke` because the Rust side returns
   * a bare `f64`, not the `{ data }` envelope `ipc_invoke` unwraps.
   */
  async zoom_factor(): Promise<number> {
    return invoke<number>('zoom_factor');
  }

  /**
   * Open the OS system menu at the cursor (Windows only — no-op on macOS / Linux).
   *
   * `labels` are localized strings formatted via react-intl on the caller side —
   * Rust must never own user-facing copy
   * (see `.ai-context/desktop/rust-conventions.md` "Error Codes & i18n").
   * `&` characters inside each label are Win32 accelerator hints (e.g. `&Restore`).
   *
   * Uses raw `invoke` rather than `ipc_invoke` because the Rust side returns
   * `Result<(), String>`: `Ok(())` serializes to `null`, which the shared
   * `ipc_invoke` wrapper would treat as a missing result and reject.
   */
  async show_system_menu(labels: SystemMenuLabels): Promise<void> {
    await invoke<void>('show_window_system_menu', { labels });
  }

  /**
   * Trigger Win11 22H2+ Snap Layouts flyout. The React maximize-button hover
   * handler calls this after a ~620ms hover delay. Rust synthesizes `Win+Z`
   * via `SendInput`. No-op on macOS / Linux / Win10.
   */
  async show_snap_overlay(): Promise<void> {
    await invoke<void>('show_window_snap_overlay');
  }

  /**
   * Force-close the main window after the user confirms the in-progress-
   * recording close modal. Rust sets a `FORCE_CLOSE_REQUESTED` flag so the
   * `WindowEvent::CloseRequested` / `RunEvent::ExitRequested` handlers
   * bypass the prevent-close guard on the next pass.
   *
   * Uses raw `invoke` (Rust returns `()` which serializes to `null`).
   */
  async force_close_main_window(): Promise<void> {
    await invoke<void>('force_close_main_window');
  }

  /**
   * Tell Rust whether the frontend close guard is mounted and ready to
   * handle `app://close-requested`. The Rust close/exit handlers fail open
   * (allow native close) until this is `true`, so an unmounted guard can
   * never trap the user in a window that refuses to close.
   */
  async set_close_guard_ready(ready: boolean): Promise<void> {
    await invoke<void>('set_close_guard_ready', { ready });
  }

  /**
   * Tell Rust to allow the next navigation event to proceed.
   * Used when the user confirms they want to reload despite a recording warning.
   */
  async approve_next_reload(): Promise<void> {
    await invoke<void>('approve_next_reload');
  }

  /**
   * Sync the navigation guard feature flag to Rust.
   * If false, the Rust on_navigation hook will allow all reloads without checking.
   */
  async set_navigation_guard_enabled(enabled: boolean): Promise<void> {
    await invoke<void>('set_navigation_guard_enabled', { enabled });
  }

  /**
   * Arm/disarm the shell's `window.open` popup gate for an embedded OAuth
   * flow. The Nango Connect UI opens the provider OAuth window with
   * `window.open` from its cross-origin iframe, which the shell denies
   * unless this gate is armed (see `on_new_window` in `src-tauri/src/lib.rs`).
   * Arm right before mounting the OAuth surface; disarm when it settles.
   */
  async set_oauth_popup_allowed(allowed: boolean): Promise<void> {
    await invoke<void>('set_oauth_popup_allowed', { allowed });
  }
}

export const tauriWindowController = new TauriWindowController();
